/* eslint-disable no-param-reassign */
/* @ngInject */

export default (
  $q,
  $state,
  $rootScope,
  $scope,
  webpage,
  organizationService,
  accountService,
  authenticationService,
  uiService,
  navigationService,
  OrganizationPermissions,
  INSTANCE
) => {
  $scope.Model = {
    INSTANCE,
    AUTH: authenticationService,
    oldPassword: '',
    newPassword: '',
    verificationPassword: '',
    passwordChangeInfo: [],
    twoFactorFindOutMore: false,
    twoFactorSetUpInProgress: false,
    twoFactorValidation: '',
    isTwoFactorTOTP: true,
    isPasswordChanged: false,
    isWorking: false,
    isReady: false
  };

  /*
   * Initialize controller and start loading the required data
   * --------------------------------------------------------------------------------
   */
  webpage.pageTitle('Loading, please wait');
  $q.all([
    organizationService.readOrganization({ id: $rootScope.CurrentOrganization.id }),
    accountService.readAccount({ id: $rootScope.CurrentAccount.id })
  ])
    .then((data) => {
      // Overwrite $rootScope values in case they've reverted to values within Token
      $rootScope.CurrentOrganization.permissions = OrganizationPermissions.build(
        data[0].permissions
      );
      $rootScope.CurrentAccount.authentication.twoFactorEnabled =
        data[1].authentication.twoFactorEnabled;
    })
    .then(() => {
      // Declare Model as Ready and start rendering the page
      webpage.pageTitle('My password and security : Settings');

      let passwordChangeInfo = 'Password is case-sensitive and must be';
      if (
        !$rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length ||
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length <= 8
      ) {
        passwordChangeInfo = passwordChangeInfo.concat(' at least 8 characters.');
      }
      if ($rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length > 8) {
        passwordChangeInfo = passwordChangeInfo.concat(
          ` longer than ${$rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length} character(s).`
        );
      }
      if (
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.lowercase ||
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.uppercase ||
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.digits ||
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.symbols
      ) {
        passwordChangeInfo = passwordChangeInfo.concat(' Password also must include at least');
        if ($rootScope.CurrentOrganization?.permissions?.passwordPolicy?.lowercase) {
          passwordChangeInfo = passwordChangeInfo.concat(
            ' one lowercase character (eg. a, b, c);'
          );
        }
        if ($rootScope.CurrentOrganization?.permissions?.passwordPolicy?.uppercase) {
          passwordChangeInfo = passwordChangeInfo.concat(
            ' one uppercase character (eg. A, B, C);'
          );
        }
        if ($rootScope.CurrentOrganization?.permissions?.passwordPolicy?.digits) {
          passwordChangeInfo = passwordChangeInfo.concat(' one digit (eg. 1, 2, 3);');
        }
        if ($rootScope.CurrentOrganization?.permissions?.passwordPolicy?.symbols) {
          passwordChangeInfo = passwordChangeInfo.concat(' one symbol (eg. !, @, #);');
        }
      }
      $scope.Model.passwordChangeInfo = [
        'In order to better protect your data, we have a password policy',
        passwordChangeInfo
      ];
      $scope.Model.isReady = true;
    });

  /*
   * Methods available in Controller Scope
   * --------------------------------------------------------------------------------
   */
  $scope.previous = () => {
    if ($rootScope.CurrentAccount.hasAccountDetails()) {
      if ($rootScope.CurrentAccount.isBroker) {
        $state.transitionTo('settings.my-accreditations');
      } else {
        $state.transitionTo('settings.my-account');
      }
    }
  };
  $scope.next = () => {
    if ($scope.Model.isPasswordChanged) {
      $state.transitionTo('organizationSwitch');
      navigationService.organizationChange({
        idOrganization: $rootScope.CurrentOrganization.id,
        url: '/'
      });
    }
  };
  $scope.changePassword = () => {
    if (
      $scope.Model.oldPassword !== '' &&
      $scope.Model.newPassword !== '' &&
      $scope.Model.verificationPassword !== ''
    ) {
      const minLength =
        $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length > 8
          ? $rootScope.CurrentOrganization?.permissions?.passwordPolicy?.length
          : 8;
      if ($scope.Model.newPassword === $scope.Model.verificationPassword) {
        if ($scope.Model.oldPassword !== $scope.Model.newPassword) {
          if ($scope.Model.newPassword.length >= minLength) {
            $scope.Model.isWorking = true;
            authenticationService
              .passwordChange({
                oldPassword: $scope.Model.oldPassword,
                newPassword: $scope.Model.newPassword
              })
              .then(function (payload) {
                if (payload) {
                  uiService.toast({
                    text: {
                      p1: 'Password',
                      p2: 'changed',
                      p3: 'successfully'
                    },
                    warn: true
                  });
                  $scope.Model.isPasswordChanged = true;
                } else {
                  uiService.toast({
                    text: {
                      p1: 'Current password is incorrect',
                      p2: 'or',
                      p3: 'new password does not match the policy'
                    },
                    warn: true
                  });
                }
                $scope.Model.isWorking = false;
                $scope.Model.oldPassword = '';
                $scope.Model.newPassword = '';
                $scope.Model.verificationPassword = '';
              });
          } else {
            uiService.toast({
              text: {
                p1: 'Password must be',
                p2: `at least ${minLength}`,
                p3: 'characters long'
              },
              warn: true
            });
          }
        } else {
          uiService.toast({
            text: {
              p1: 'New password',
              p2: 'must be different',
              p3: 'from the old password'
            },
            warn: true
          });
        }
      } else {
        uiService.toast({
          text: {
            p1: 'New password and',
            p2: "it's confirmation",
            p3: 'do not match'
          },
          warn: true
        });
      }
    } else {
      uiService.toast({
        text: {
          p1: 'All',
          p2: 'password fields',
          p3: 'are required'
        },
        warn: true
      });
    }
  };

  $scope.twoFactorTypeToggle = () => {
    if (!$scope.Model.isTwoFactorTOTP) {
      authenticationService.twoFactorSMS();
    }
  };

  $scope.twoFactorActivate = () => {
    const resolver = $scope.Model.isTwoFactorTOTP
      ? authenticationService.twoFactorValidate
      : authenticationService.twoFactorSMSValidate;

    resolver({ totp: $scope.Model.twoFactorValidation }).then((valid) => {
      if (!valid) {
        uiService.toast({
          text: {
            p1: 'Code',
            p2: 'you entered',
            p3: 'is not valid'
          },
          warn: true
        });
      } else {
        uiService.scopeApply(() => {
          $rootScope.CurrentAccount.authentication.twoFactorEnabled = true;
          $scope.Model.twoFactorSetUpInProgress = false;
        }, 100);
        uiService.toast({
          text: {
            p1: 'Two-factor authentication',
            p2: 'activated',
            p3: 'successfully'
          }
        });
      }
    });
  };

  $scope.twoFactorDeactivate = () => {
    authenticationService.twoFactorToggle().then((result) => {
      if (result === true) {
        uiService.scopeApply(() => {
          $rootScope.CurrentAccount.authentication.twoFactorEnabled = false;
          $scope.Model.twoFactorSetUpInProgress = false;
        }, 100);
        uiService.toast({
          text: {
            p1: 'Two-factor authentication',
            p2: 'deactivated',
            p3: 'successfully'
          }
        });
      }
    });
  };
};
